import { useEffect, useState } from "react";
import { Card, Progress, Spinner } from "flowbite-react";
import {
  FaMicrochip,
  FaMemory,
  FaHdd,
  FaArrowUp,
  FaArrowDown,
  FaClock,
  FaNetworkWired,
} from "react-icons/fa";
import axios from "axios";

function formatBytes(bytes) {
  if (bytes === 0) return "0 B";
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
}

const VPSMonitorPage = ({ isActive }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const RAM_MAX = 32 * 1024 ** 3; // 32GB
  const DISK_MAX = 400 * 1024 ** 3; // 400GB
  const BANDWIDTH_MAX = 32 * 1024 ** 4; // 32TB

  useEffect(() => {
    let interval;

    const fetchMetrics = async () => {
      if (!isActive) return;

      try {
        const dateTo = new Date().toISOString();
        const dateFrom = new Date(Date.now() - 30 * 60 * 1000).toISOString();

        const url = `https://developers.hostinger.com/api/vps/v1/virtual-machines/700845/metrics?date_from=${encodeURIComponent(
          dateFrom
        )}&date_to=${encodeURIComponent(dateTo)}`;

        const res = await axios.get(url, {
          headers: {
            Authorization:
              "Bearer fvxItiC7CT5PGt8zZZp3YO1agUUtW4knk5eCXD3Tf114645b",
          },
        });

        setData(res.data);
      } catch (err) {
        console.error("Erro ao buscar métricas:", err);
      } finally {
        setLoading(false);
      }
    };

    if (isActive) {
      fetchMetrics();
      interval = setInterval(fetchMetrics, 30000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isActive]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-40">
        <Spinner size="xl" />
      </div>
    );
  }

  if (!data) {
    return <p className="text-center text-red-500">Erro ao carregar dados</p>;
  }

  const timestamp = Object.keys(data?.cpu_usage?.usage)?.[0];

  const cpu = data?.cpu_usage?.usage?.[timestamp];
  const ram = data?.ram_usage?.usage?.[timestamp];
  const disk = data?.disk_space?.usage?.[timestamp];
  const out = data?.outgoing_traffic?.usage?.[timestamp];
  const inc = data?.incoming_traffic?.usage?.[timestamp];
  const totalTraffic = out + inc;

  const cards = [
    {
      title: "Uso de CPU",
      value: `${cpu.toFixed(2)}%`,
      icon: <FaMicrochip className="text-blue-600 w-6 h-6" />,
      progress: cpu,
      color: "blue",
    },
    {
      title: "Memória RAM",
      value: `${formatBytes(ram)} / 32 GB`,
      icon: <FaMemory className="text-purple-600 w-6 h-6" />,
      progress: (ram / RAM_MAX) * 100,
      color: "purple",
    },
    {
      title: "Espaço em Disco",
      value: `${formatBytes(disk)} / 400 GB`,
      icon: <FaHdd className="text-green-600 w-6 h-6" />,
      progress: (disk / DISK_MAX) * 100,
      color: "green",
    },
    {
      title: "Tráfego de Saída",
      value: formatBytes(out),
      icon: <FaArrowUp className="text-amber-600 w-6 h-6" />,
    },
    {
      title: "Tráfego de Entrada",
      value: formatBytes(inc),
      icon: <FaArrowDown className="text-cyan-600 w-6 h-6" />,
    },
    {
      title: "Largura de Banda",
      value: `${formatBytes(totalTraffic)} / 32 TB`,
      icon: <FaNetworkWired className="text-pink-600 w-6 h-6" />,
      progress: (totalTraffic / BANDWIDTH_MAX) * 100,
      color: "pink",
    },
  ];

  return (
    <div className=" mx-auto px-4">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6 mt-2">
        Monitoramento da VPS
      </h2>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((card, index) => (
          <Card
            key={index}
            className="shadow hover:shadow-lg transition-all duration-300 border border-gray-100"
          >
            <div className="flex items-center justify-between mb-2">
              <div>
                <h5 className="text-md font-semibold text-gray-700">
                  {card.title}
                </h5>
                <p className="text-sm text-gray-500">{card.value}</p>
              </div>
              {card.icon}
            </div>
            {card.progress !== undefined && (
              <Progress
                progress={Math.min(card.progress, 100)}
                color={card.color}
                className="mt-2"
              />
            )}
          </Card>
        ))}
      </div>
    </div>
  );
};

export default VPSMonitorPage;
